<script setup lang="ts">
import TplModal from "./TplModal.vue";
import { useI18n } from "../../composables";
import { useCloudI18nStrict } from "../../composables";
import { blockTypeIcons } from "../../utils/blockTypeIcons";
import {
  SAVED_MODULES_HEADLESS_KEY,
  EDITOR_KEY,
  requireInject,
} from "../../keys";
import type { SavedModule } from "@aswin.dev/types";
import { Package, Search, Trash2, X } from "@lucide/vue";
import { computed, defineAsyncComponent, ref, watch } from "vue";

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "insert", module: SavedModule, insertIndex: number | undefined): void;
}>();

const ModulePreviewCanvas = defineAsyncComponent(
  () => import("./ModulePreviewCanvas.vue"),
);

const { t } = useI18n();
const { t: cloudT } = useCloudI18nStrict();
const savedModules = requireInject(
  SAVED_MODULES_HEADLESS_KEY,
  "ModuleBrowserModal",
);
const editor = requireInject(EDITOR_KEY, "ModuleBrowserModal");

const searchQuery = ref("");
const selectedModuleId = ref<string | null>(null);
const confirmDeleteId = ref<string | null>(null);
// 'end' = append, 'beginning' = index 0, or block id = after that block
const insertPosition = ref<string>("end");

const filteredModules = computed(() => {
  const modules = savedModules.modules.value;
  if (!searchQuery.value) return modules;
  const query = searchQuery.value.toLowerCase();
  return modules.filter((m) => m.name.toLowerCase().includes(query));
});

const selectedModule = computed(() => {
  if (!selectedModuleId.value) return null;
  return (
    savedModules.modules.value.find((m) => m.id === selectedModuleId.value) ??
    null
  );
});

interface PositionOption {
  value: string;
  label: string;
}

const positionOptions = computed<PositionOption[]>(() => {
  const options: PositionOption[] = [
    { value: "beginning", label: cloudT.modules.insertAtBeginning },
  ];
  const blocks = editor.content.value.blocks;
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const typeKey = block.type as keyof typeof t.blocks;
    const label = t.blocks[typeKey] ?? block.type;
    options.push({
      value: block.id,
      label: cloudT.modules.insertAfterBlock.replace(
        "{block}",
        `${label} ${i + 1}`,
      ),
    });
  }
  options.push({ value: "end", label: cloudT.modules.insertAtEnd });
  return options;
});

const resolvedInsertIndex = computed<number | undefined>(() => {
  if (insertPosition.value === "end") return undefined;
  if (insertPosition.value === "beginning") return 0;
  const blocks = editor.content.value.blocks;
  const idx = blocks.findIndex((b) => b.id === insertPosition.value);
  if (idx !== -1) return idx + 1;
  return undefined;
});

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      searchQuery.value = "";
      selectedModuleId.value = null;
      confirmDeleteId.value = null;
      // Default to after selected block, or end
      const selectedId = editor.state.selectedBlockId;
      if (selectedId) {
        const idx = editor.content.value.blocks.findIndex(
          (b) => b.id === selectedId,
        );
        insertPosition.value = idx !== -1 ? selectedId : "end";
      } else {
        insertPosition.value = "end";
      }
    }
  },
);

function getModuleBlockTypeIcons(
  module: SavedModule,
): { type: string; icon: unknown }[] {
  const icons: { type: string; icon: unknown }[] = [];
  const seen = new Set<string>();
  for (const block of module.content) {
    if (!seen.has(block.type) && blockTypeIcons[block.type]) {
      seen.add(block.type);
      icons.push({ type: block.type, icon: blockTypeIcons[block.type] });
    }
    if (icons.length >= 5) break;
  }
  return icons;
}

function getRemainingTypeCount(module: SavedModule): number {
  const types = new Set(module.content.map((b) => b.type));
  return Math.max(0, types.size - 5);
}

async function handleDelete(moduleId: string): Promise<void> {
  try {
    await savedModules.deleteModule(moduleId);
    if (selectedModuleId.value === moduleId) {
      selectedModuleId.value = null;
    }
  } finally {
    confirmDeleteId.value = null;
  }
}

function handleInsert(): void {
  if (selectedModule.value) {
    emit("insert", selectedModule.value, resolvedInsertIndex.value);
  }
}

function handleClose(): void {
  emit("close");
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    handleClose();
  }
  if (event.key === "Enter" && selectedModule.value) {
    event.preventDefault();
    handleInsert();
  }
}
</script>

<template>
  <TplModal :visible="visible" @close="handleClose" @keydown="handleKeydown">
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tpl-module-browser-title"
      class="tpl-scale-in tpl:mx-4 tpl:flex tpl:w-full tpl:max-w-[1000px] tpl:flex-col tpl:rounded-[var(--tpl-radius-lg)]"
      style="
        background-color: var(--tpl-bg-elevated);
        box-shadow: var(--tpl-shadow-xl);
        max-height: 90vh;
      "
    >
      <!-- Header -->
      <div
        class="tpl:flex tpl:items-center tpl:justify-between tpl:border-b tpl:px-5 tpl:py-4 tpl:border-[var(--tpl-border)]"
      >
        <h3
          id="tpl-module-browser-title"
          class="tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text)]"
        >
          {{ cloudT.modules.browse }}
        </h3>
        <button
          :aria-label="cloudT.modules.close"
          class="tpl:cursor-pointer tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:p-1 tpl:transition-colors tpl:duration-100 tpl:text-[var(--tpl-text-dim)]"
          @click="handleClose"
        >
          <X :size="16" :stroke-width="2" />
        </button>
      </div>

      <!-- Body -->
      <div class="tpl:flex tpl:min-h-0 tpl:flex-1 tpl:overflow-hidden">
        <!-- Left panel: module grid -->
        <div
          class="tpl:flex tpl:w-[300px] tpl:shrink-0 tpl:flex-col tpl:overflow-hidden"
        >
          <!-- Search -->
          <div class="tpl:px-4 tpl:pt-4 tpl:pb-3">
            <div class="tpl:relative">
              <Search
                :size="14"
                :stroke-width="2"
                class="tpl:pointer-events-none tpl:absolute tpl:left-3 tpl:top-1/2 tpl:-translate-y-1/2 tpl:text-[var(--tpl-text-dim)]"
              />
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="cloudT.modules.search"
                class="tpl:h-9 tpl:w-full tpl:rounded-md tpl:border tpl:pl-9 tpl:pr-3 tpl:text-sm tpl:outline-none tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:text-[var(--tpl-text)]"
              />
            </div>
          </div>

          <!-- Grid -->
          <div class="tpl:flex-1 tpl:overflow-y-auto tpl:px-4 tpl:pb-4">
            <div
              v-if="filteredModules.length > 0"
              class="tpl:flex tpl:flex-col tpl:gap-1"
            >
              <button
                v-for="mod in filteredModules"
                :key="mod.id"
                type="button"
                :aria-pressed="selectedModuleId === mod.id"
                class="tpl:group/card tpl:w-full tpl:cursor-pointer tpl:rounded-[var(--tpl-radius-md)] tpl:border tpl:bg-transparent tpl:px-3 tpl:py-2 tpl:text-left tpl:transition-all tpl:duration-[120ms]"
                :style="{
                  borderColor:
                    selectedModuleId === mod.id
                      ? 'var(--tpl-primary)'
                      : 'var(--tpl-border)',
                  backgroundColor:
                    selectedModuleId === mod.id
                      ? 'var(--tpl-primary-light)'
                      : 'transparent',
                }"
                @click="selectedModuleId = mod.id"
              >
                <div class="tpl:flex tpl:items-center tpl:gap-2">
                  <span
                    class="tpl:flex-1 tpl:truncate tpl:text-xs tpl:font-semibold tpl:text-[var(--tpl-text)]"
                  >
                    {{ mod.name }}
                  </span>
                  <span
                    class="tpl:shrink-0 tpl:rounded-full tpl:px-1.5 tpl:py-0.5 tpl:text-[10px] tpl:font-medium tpl:bg-[var(--tpl-bg-hover)] tpl:text-[var(--tpl-text-muted)]"
                  >
                    {{
                      cloudT.modules.blockCount.replace(
                        "{count}",
                        String(mod.content.length),
                      )
                    }}
                  </span>
                </div>
                <div class="tpl:mt-1 tpl:flex tpl:items-center tpl:gap-1">
                  <component
                    :is="icon.icon"
                    v-for="icon in getModuleBlockTypeIcons(mod)"
                    :key="icon.type"
                    :size="14"
                    :stroke-width="1.5"
                    class="tpl:text-[var(--tpl-text-dim)]"
                  />
                  <span
                    v-if="getRemainingTypeCount(mod) > 0"
                    class="tpl:text-[10px] tpl:text-[var(--tpl-text-dim)]"
                  >
                    +{{ getRemainingTypeCount(mod) }}
                  </span>
                  <button
                    v-if="confirmDeleteId === mod.id"
                    :aria-label="cloudT.modules.deleteConfirm"
                    class="tpl:ml-auto tpl:cursor-pointer tpl:rounded-md tpl:border tpl:px-2 tpl:py-0.5 tpl:text-[10px] tpl:font-medium tpl:transition-colors tpl:duration-100 tpl:border-[var(--tpl-danger)] tpl:text-[var(--tpl-danger)]"
                    style="background-color: transparent"
                    @click.stop="handleDelete(mod.id)"
                  >
                    {{ cloudT.modules.deleteConfirm }}
                  </button>
                  <button
                    v-else
                    class="tpl-module-delete-btn tpl:ml-auto tpl:cursor-pointer tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:p-0.5 tpl:transition-colors tpl:duration-100 tpl:text-[var(--tpl-text-dim)]"
                    :aria-label="cloudT.modules.delete"
                    :title="cloudT.modules.delete"
                    @click.stop="confirmDeleteId = mod.id"
                  >
                    <Trash2 :size="12" :stroke-width="1.5" />
                  </button>
                </div>
              </button>
            </div>

            <!-- Empty state -->
            <div
              v-else
              class="tpl:flex tpl:flex-col tpl:items-center tpl:justify-center tpl:py-12"
            >
              <Package
                :size="32"
                :stroke-width="1"
                class="tpl:text-[var(--tpl-text-dim)]"
              />
              <p class="tpl:mt-2 tpl:text-xs tpl:text-[var(--tpl-text-dim)]">
                {{
                  searchQuery
                    ? cloudT.modules.noModules
                    : cloudT.modules.noModulesHint
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Right panel: preview -->
        <div
          class="tpl:flex tpl:flex-1 tpl:flex-col tpl:overflow-hidden tpl:border-l tpl:border-[var(--tpl-border)]"
        >
          <div
            v-if="selectedModule"
            class="tpl:flex tpl:flex-1 tpl:flex-col tpl:overflow-hidden"
          >
            <!-- Visual preview -->
            <div class="tpl:flex-1 tpl:overflow-y-auto tpl:p-4">
              <ModulePreviewCanvas :blocks="selectedModule.content" />
            </div>
          </div>

          <!-- Empty preview state -->
          <div
            v-else
            class="tpl:flex tpl:flex-1 tpl:flex-col tpl:items-center tpl:justify-center tpl:px-4"
          >
            <Package
              :size="32"
              :stroke-width="1"
              class="tpl:text-[var(--tpl-text-dim)]"
            />
            <p
              class="tpl:mt-2 tpl:text-center tpl:text-xs tpl:text-[var(--tpl-text-dim)]"
            >
              {{ cloudT.modules.selectToPreview }}
            </p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="tpl:flex tpl:items-center tpl:justify-between tpl:border-t tpl:px-5 tpl:py-3 tpl:border-[var(--tpl-border)]"
      >
        <div class="tpl:flex tpl:items-center tpl:gap-2">
          <label
            class="tpl:shrink-0 tpl:text-xs tpl:text-[var(--tpl-text-dim)]"
          >
            {{ cloudT.modules.insertPosition }}
          </label>
          <select
            v-model="insertPosition"
            class="tpl:h-7 tpl:max-w-[220px] tpl:rounded-md tpl:border tpl:px-2 tpl:text-xs tpl:outline-none tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:text-[var(--tpl-text)]"
          >
            <option
              v-for="opt in positionOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="tpl:flex tpl:gap-2">
          <button
            type="button"
            class="tpl:cursor-pointer tpl:rounded-md tpl:border tpl:px-3 tpl:py-1.5 tpl:text-sm tpl:font-medium tpl:shadow-xs tpl:transition-all tpl:duration-150 tpl:border-[var(--tpl-border)] tpl:text-[var(--tpl-text)] tpl:bg-[var(--tpl-bg)]"
            @click="handleClose"
          >
            {{ cloudT.modules.close }}
          </button>
          <button
            type="button"
            class="tpl:cursor-pointer tpl:rounded-md tpl:px-3 tpl:py-1.5 tpl:text-sm tpl:font-medium tpl:shadow-xs tpl:transition-all tpl:duration-150 tpl:hover:opacity-90 tpl:disabled:cursor-not-allowed tpl:disabled:opacity-50 tpl:bg-[var(--tpl-primary)] tpl:text-[var(--tpl-bg)]"
            :disabled="!selectedModule"
            @click="handleInsert"
          >
            {{ cloudT.modules.insert }}
          </button>
        </div>
      </div>
    </div>
  </TplModal>
</template>

<style>
.tpl-module-delete-btn:hover {
  color: var(--tpl-danger) !important;
}
</style>
